<template>
  <ContentWrap title="红包活动配置" message="配置领取规则、库存及弹窗全部文案">
    <el-alert
      class="mb-16px"
      type="info"
      :closable="false"
      show-icon
      title="保存后，用户下次打开红包或领取时生效。金额按元填写；活动时间统一使用上海时间。库存和领取次数由服务端校验，余额与领取金额来自真实入账。非必填文案可留空隐藏。"
    />
    <el-alert
      v-if="loadFailed"
      class="mb-16px"
      type="error"
      :closable="false"
      title="配置加载失败，请重新加载后再编辑。"
    />
    <el-form
      v-loading="loading"
      :model="form"
      label-width="150px"
      class="packet-form"
      :disabled="busy || !loaded"
    >
      <el-divider content-position="left">活动与领取规则</el-divider>
      <el-form-item label="开启红包"><el-switch v-model="form.enabled" /></el-form-item>
      <el-form-item label="最小金额（元）">
        <el-input-number v-model="minYuan" :min="0.01" :max="1000" :step="0.01" :precision="2" />
      </el-form-item>
      <el-form-item label="最大金额（元）">
        <el-input-number v-model="maxYuan" :min="0.01" :max="1000" :step="0.01" :precision="2" />
      </el-form-item>
      <el-form-item label="每人每日领取上限">
        <el-input-number v-model="form.dailyLimit" :min="0" :max="10000" :precision="0" />
        <span class="tip">次，0 表示不限</span>
      </el-form-item>
      <el-form-item label="每人累计领取上限">
        <el-input-number v-model="form.totalLimit" :min="0" :max="100000000" :precision="0" />
        <span class="tip"
          >次，0 表示不限；旧数据仅可确认至少领取 1
          次，精确累计无法恢复；累计限制按迁移后记录加旧数据至少 1 次统计</span
        >
      </el-form-item>
      <el-form-item label="每日红包总数">
        <el-input-number v-model="form.dailyStock" :min="0" :max="100000000" :precision="0" />
        <span class="tip"
          >个，0 表示不限。剩余量由后端扣除当日已领取数量计算，不能直接设置剩余量。</span
        >
      </el-form-item>
      <el-form-item label="活动开始时间">
        <el-date-picker
          v-model="form.startTime"
          type="datetime"
          value-format="YYYY-MM-DD HH:mm:ss"
          placeholder="留空表示不限"
          clearable
        />
      </el-form-item>
      <el-form-item label="活动结束时间">
        <el-date-picker
          v-model="form.endTime"
          type="datetime"
          value-format="YYYY-MM-DD HH:mm:ss"
          placeholder="留空表示不限"
          clearable
        />
      </el-form-item>
      <el-form-item label="规则预览"
        ><span>{{ rulePreview }}</span></el-form-item
      >
      <template v-for="group in textGroups" :key="group.title">
        <el-divider content-position="left">{{ group.title }}</el-divider>
        <el-form-item v-for="field in group.fields" :key="field.key" :label="field.label">
          <div class="text-field">
            <el-input
              v-model="form[field.key]"
              :type="field.multiline ? 'textarea' : 'text'"
              :rows="2"
              :maxlength="500"
              show-word-limit
            />
            <div v-if="field.help" class="tip field-help">{{ field.help }}</div>
          </div>
        </el-form-item>
      </template>
    </el-form>
    <div class="actions">
      <el-button
        v-hasPermi="['bosscircle:red-packet:update', 'bosscircle:wallet:grant']"
        type="primary"
        :loading="saving"
        :disabled="busy || !loaded"
        @click="save"
        >保存配置</el-button
      >
      <el-button
        v-hasPermi="['bosscircle:red-packet:query', 'bosscircle:wallet:query']"
        :loading="loading"
        :disabled="busy"
        @click="load"
        >重新加载</el-button
      >
      <el-button
        v-hasPermi="['bosscircle:red-packet:update', 'bosscircle:wallet:grant']"
        type="danger"
        plain
        :loading="resetting"
        :disabled="busy || !loaded"
        @click="reset"
        >恢复全部默认</el-button
      >
    </div>
  </ContentWrap>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { getRedPacketRule, resetRedPacketRule, updateRedPacketRule } from '@/api/business/redPacket'
import type { RedPacketRule } from '@/api/business/redPacket'

defineOptions({ name: 'BusinessRedPacket' })
type TextKey = {
  [K in keyof RedPacketRule]: RedPacketRule[K] extends string ? K : never
}[keyof RedPacketRule]
interface TextField {
  key: TextKey
  label: string
  help?: string
  multiline?: boolean
}
const textGroups: { title: string; fields: TextField[] }[] = [
  {
    title: '领取弹窗文案',
    fields: [
      { key: 'title', label: '标题（必填）' },
      {
        key: 'subtitleTemplate',
        label: '规则文案模板',
        help: '{min}、{max} 为真实金额上下限（元），{limit} 为服务端根据每日与累计领取上限生成的规则。请保留变量，避免文案与规则不一致。'
      },
      { key: 'description', label: '引导说明', multiline: true },
      {
        key: 'validityText',
        label: '余额有效性说明',
        help: '请与实际业务一致：领取后计入红包余额，不会因跨日自动失效。'
      },
      { key: 'claimText', label: '领取按钮（必填）' },
      { key: 'openingText', label: '拆红包文字（必填）' },
      { key: 'claimingText', label: '领取加载（必填）' },
      {
        key: 'stockTemplate',
        label: '有限库存模板',
        help: '{remaining} 为后端计算的当日真实剩余红包个数，不能填写虚构数量。'
      },
      { key: 'unlimitedStockText', label: '不限库存说明' },
      { key: 'footerText', label: '底部说明', multiline: true }
    ]
  },
  {
    title: '领取成功文案',
    fields: [
      { key: 'successTitle', label: '成功标题' },
      { key: 'successDescription', label: '成功说明', multiline: true },
      { key: 'balanceLabel', label: '余额标签' },
      { key: 'balanceNote', label: '余额说明' },
      {
        key: 'currencySymbol',
        label: '人民币符号',
        help: '可留空，最多 8 字，仅控制人民币金额单位的展示，不改变实际币种。'
      },
      { key: 'successButtonText', label: '成功按钮（必填）' }
    ]
  },
  {
    title: '不可领取提示',
    fields: [
      { key: 'closedMessage', label: '活动关闭或未开放' },
      { key: 'soldOutMessage', label: '当日红包领完' },
      { key: 'limitMessage', label: '达到领取上限' }
    ]
  }
]
const form = ref<RedPacketRule>({
  enabled: true,
  minFen: 188,
  maxFen: 988,
  dailyLimit: 1,
  totalLimit: 0,
  dailyStock: 0,
  startTime: null,
  endTime: null,
  title: '新人见面礼',
  subtitleTemplate: '{min}～{max}元随机 · {limit}',
  description: '拆开红包，看看今天的贵人运',
  validityText: '领取后计入红包余额',
  claimText: '立即领取',
  openingText: '抢',
  claimingText: '领取中…',
  balanceNote: '暂不支持提现',
  currencySymbol: '¥',
  stockTemplate: '今日红包剩余 {remaining} 个',
  unlimitedStockText: '红包持续发放中',
  footerText: '红包由平台发放，不可提现，可用于平台内权益兑换',
  successTitle: '恭喜获得',
  successDescription: '红包已存入您的账户\n可在「我的」随时查看',
  balanceLabel: '当前红包余额',
  successButtonText: '知道了',
  closedMessage: '红包活动暂未开放',
  soldOutMessage: '今日红包已领完，请明天再来',
  limitMessage: '已达到红包领取上限'
})
const loading = ref(false)
const saving = ref(false)
const resetting = ref(false)
const loaded = ref(false)
const loadFailed = ref(false)
const busy = computed(() => loading.value || saving.value || resetting.value)
const minYuan = computed({
  get: () => form.value.minFen / 100,
  set: (value: number | undefined) => {
    form.value.minFen = value == null ? NaN : Math.round(value * 100)
  }
})
const maxYuan = computed({
  get: () => form.value.maxFen / 100,
  set: (value: number | undefined) => {
    form.value.maxFen = value == null ? NaN : Math.round(value * 100)
  }
})
const rulePreview = computed(
  () =>
    `${minYuan.value.toFixed(2)}～${maxYuan.value.toFixed(2)} 元随机；${form.value.dailyLimit ? `每人每日 ${form.value.dailyLimit} 次` : '每日次数不限'}；${form.value.totalLimit ? `每人累计 ${form.value.totalLimit} 次` : '累计次数不限'}；${form.value.dailyStock ? `每日发放 ${form.value.dailyStock} 个` : '每日库存不限'}`
)

const load = async () => {
  if (loading.value || saving.value) return
  loading.value = true
  loadFailed.value = false
  loaded.value = false
  try {
    const data = await getRedPacketRule()
    if (!data) throw new Error('Empty configuration')
    form.value = { ...data, startTime: data.startTime || null, endTime: data.endTime || null }
    loaded.value = true
  } catch {
    loadFailed.value = true
    ElMessage.error('红包配置加载失败')
  } finally {
    loading.value = false
  }
}
const save = async () => {
  if (busy.value || !loaded.value) return
  const data = form.value
  if (
    ![data.minFen, data.maxFen].every((v) => Number.isInteger(v) && v >= 1 && v <= 100000) ||
    data.maxFen < data.minFen
  ) {
    ElMessage.warning('请填写 0.01～1000 元的金额，最大金额不能小于最小金额')
    return
  }
  if (
    !Number.isInteger(data.dailyLimit) ||
    data.dailyLimit < 0 ||
    data.dailyLimit > 10000 ||
    ![data.totalLimit, data.dailyStock].every(
      (v) => Number.isInteger(v) && v >= 0 && v <= 100000000
    )
  ) {
    ElMessage.warning('请填写有效的整数次数与库存')
    return
  }
  if (data.startTime && data.endTime && data.startTime >= data.endTime) {
    ElMessage.warning('结束时间必须晚于开始时间')
    return
  }
  if (textGroups.some((group) => group.fields.some((field) => data[field.key].length > 500))) {
    ElMessage.warning('每项文案最多 500 字')
    return
  }
  const requiredFields: TextKey[] = [
    'title',
    'claimText',
    'claimingText',
    'openingText',
    'successButtonText'
  ]
  if (requiredFields.some((key) => !data[key].trim())) {
    ElMessage.warning('请填写标题、领取按钮、领取加载按钮、拆红包文字和成功按钮')
    return
  }
  if (data.currencySymbol.length > 8) {
    ElMessage.warning('人民币展示符号最多 8 字')
    return
  }
  saving.value = true
  try {
    await updateRedPacketRule({
      ...data,
      startTime: data.startTime || null,
      endTime: data.endTime || null
    })
    ElMessage.success('已保存，用户下次打开红包或领取时生效')
  } catch {
    ElMessage.error('保存失败')
  } finally {
    saving.value = false
  }
}
const reset = async () => {
  if (busy.value || !loaded.value) return
  try {
    await ElMessageBox.confirm(
      '将恢复全部规则及文案的默认配置（含活动开关、金额、领取上限、库存和时间），确认继续？',
      '恢复全部默认',
      { type: 'warning' }
    )
  } catch {
    return
  }
  resetting.value = true
  try {
    await resetRedPacketRule()
    await load()
    if (loaded.value) ElMessage.success('已恢复默认')
  } catch {
    ElMessage.error('恢复默认失败')
  } finally {
    resetting.value = false
  }
}
onMounted(load)
</script>

<style scoped>
.packet-form {
  max-width: 900px;
}

.tip {
  margin-left: 10px;
  font-size: 12px;
  line-height: 1.5;
  color: var(--el-text-color-secondary);
}

.text-field {
  width: 100%;
}

.field-help {
  margin: 5px 0 0;
}

.actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin: 20px 0 0 150px;
}

.actions :deep(.el-button) {
  margin-left: 0;
}

@media (width <= 600px) {
  .actions {
    margin-left: 0;
  }
}
</style>
